import React, { useState } from "react";
import { Interaction } from "../../models";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableSortLabel from "@mui/material/TableSortLabel";
import { format } from "date-fns";
import { getFirstSentence } from "../../utils"; // Import your helper function
import { styled } from "@mui/material/styles";

interface InteractionTableProps {
  interactions: Interaction[];
  onInteractionClick: (interaction: Interaction) => void;
}

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "70vh",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200], // Light background for sticky headers
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 2,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover, // Striped effect
  },
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.info.light, // Hover effect
  },
}));

const InteractionTable: React.FC<InteractionTableProps> = ({
  interactions,
  onInteractionClick,
}) => {
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof Interaction>("date");

  const handleRequestSort = (property: keyof Interaction) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedInteractions = [...interactions].sort((a, b) => {
    if (orderBy === "date") {
      return order === "asc"
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    } else {
      return order === "asc"
        ? (a[orderBy] as string).localeCompare(b[orderBy] as string)
        : (b[orderBy] as string).localeCompare(a[orderBy] as string);
    }
  });

  return (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell sortDirection={orderBy === "date" ? order : false}>
              <TableSortLabel
                active={orderBy === "date"}
                direction={orderBy === "date" ? order : "asc"}
                onClick={() => handleRequestSort("date")}
              >
                Date
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell
              sortDirection={orderBy === "title" ? order : false}
            >
              <TableSortLabel
                active={orderBy === "title"}
                direction={orderBy === "title" ? order : "asc"}
                onClick={() => handleRequestSort("title")}
              >
                Title
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell sortDirection={orderBy === "type" ? order : false}>
              <TableSortLabel
                active={orderBy === "type"}
                direction={orderBy === "type" ? order : "asc"}
                onClick={() => handleRequestSort("type")}
              >
                Type
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell>Summary</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedInteractions.map((interaction) => (
            <StyledTableRow
              key={interaction.id}
              onClick={() => onInteractionClick(interaction)}
            >
              <TableCell>
                {format(new Date(interaction.date), "MM/dd/yyyy")}
              </TableCell>
              <TableCell>{interaction.title}</TableCell>
              <TableCell>{interaction.type}</TableCell>
              <TableCell>
                {interaction.meetingSummary
                  ? getFirstSentence(interaction.meetingSummary)
                  : ""}
              </TableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default InteractionTable;
